import { TrendingUp, TrendingDown, Minus, Clock, Award, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { QuoteVersion } from '@/api/services';
import { cn } from '@/lib/utils';

interface QuoteVersionCardProps {
  version: QuoteVersion;
  isFirst: boolean;
}

export function QuoteVersionCard({ version, isFirst }: QuoteVersionCardProps) {
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not submitted';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getChangeIndicator = (change: number | null, isPercent = false) => {
    if (change === null || change === 0) {
      return (
        <span className="flex items-center text-muted-foreground text-xs">
          <Minus className="w-3 h-3 mr-1" />
          No change
        </span>
      );
    }
    if (change < 0) {
      return (
        <span className="flex items-center text-emerald-600 text-xs font-medium">
          <TrendingDown className="w-3 h-3 mr-1" />
          {Math.abs(change).toFixed(isPercent ? 1 : 2)}{isPercent ? '%' : ''} lower
        </span>
      );
    }
    return (
      <span className="flex items-center text-red-600 text-xs font-medium">
        <TrendingUp className="w-3 h-3 mr-1" />
        {change.toFixed(isPercent ? 1 : 2)}{isPercent ? '%' : ''} higher
      </span>
    );
  };

  const statusColors: Record<string, string> = {
    Draft: 'bg-gray-100 text-gray-600',
    Submitted: 'bg-blue-100 text-blue-600',
    Accepted: 'bg-emerald-100 text-emerald-600',
    Rejected: 'bg-red-100 text-red-600',
    Withdrawn: 'bg-amber-100 text-amber-600',
    Expired: 'bg-slate-100 text-slate-600',
  };

  return (
    <Card className={cn(
      'relative',
      version.isCurrentlyAwarded && 'ring-2 ring-emerald-500',
      version.isLatestVersion && 'border-primary'
    )}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono">
              v{version.version}
            </Badge>
            <Badge className={statusColors[version.quoteStatus] || 'bg-gray-100 text-gray-600'}>
              {version.quoteStatus}
            </Badge>
            {version.isLatestVersion && (
              <Badge variant="secondary" className="text-xs">
                Latest
              </Badge>
            )}
            {version.isCurrentlyAwarded && (
              <Badge className="bg-emerald-100 text-emerald-600">
                <Award className="w-3 h-3 mr-1" />
                Awarded
              </Badge>
            )}
          </div>
          {version.isContractRate && (
            <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
              Contract
            </Badge>
          )}
        </div>

        {/* Main Rate Info */}
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Base Rate</div>
            <div className="text-lg font-semibold">
              {formatCurrency(version.baseRate, version.currency)}
            </div>
            {!isFirst && version.changes && (
              <div className="mt-1">
                {getChangeIndicator(version.changes.baseRateChangePercent, true)}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Total Rate</div>
            <div className="text-lg font-semibold">
              {version.totalRate !== null
                ? formatCurrency(version.totalRate, version.currency)
                : '-'}
            </div>
            {!isFirst && version.changes && version.changes.totalRateChangePercent !== null && (
              <div className="mt-1">
                {getChangeIndicator(version.changes.totalRateChangePercent, true)}
              </div>
            )}
          </div>
        </div>

        {/* Additional Details */}
        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div>
            <div className="text-xs text-muted-foreground">Transit Days</div>
            <div className="font-medium">
              {version.transitDaysOffered !== null ? `${version.transitDaysOffered} days` : '-'}
            </div>
            {!isFirst && version.changes && version.changes.transitDaysChange !== null && version.changes.transitDaysChange !== 0 && (
              <div className="text-xs text-muted-foreground">
                {version.changes.transitDaysChange > 0 ? '+' : ''}{version.changes.transitDaysChange}d
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Fuel Surcharge</div>
            <div className="font-medium">
              {version.fuelSurchargeValue !== null
                ? `${version.fuelSurchargeValue}${version.fuelSurchargeType === 'Percentage' ? '%' : ''}`
                : '-'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Additional Charges</div>
            <div className="font-medium">
              {version.childRatesCount > 0
                ? `${version.childRatesCount} (${formatCurrency(version.childRatesTotal, version.currency)})`
                : 'None'}
            </div>
          </div>
        </div>

        {/* Change Summary */}
        {!isFirst && version.changes && (
          <div className="border-t pt-3 mt-3">
            <div className="text-xs font-medium text-muted-foreground mb-2">Changes from previous version</div>
            <div className="flex flex-wrap gap-2">
              {version.changes.newChargesAdded.length > 0 && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 text-xs">
                  +{version.changes.newChargesAdded.length} charges added
                </Badge>
              )}
              {version.changes.chargesRemoved.length > 0 && (
                <Badge variant="outline" className="bg-red-50 text-red-700 text-xs">
                  -{version.changes.chargesRemoved.length} charges removed
                </Badge>
              )}
              {version.changes.newChargesAdded.length === 0 && version.changes.chargesRemoved.length === 0 && (
                <span className="text-xs text-muted-foreground">Rate adjustments only</span>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground mt-3 pt-3 border-t">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatDate(version.submittedAt)}
          </div>
          {version.childRatesCount > 0 && (
            <div className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {version.childRatesCount} line items
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
